import { Component, OnDestroy, OnInit, Output, EventEmitter, Input, NgZone } from '@angular/core';
import { Events } from '../../helpers/events';

// ionic
import { ModalController } from '@ionic/angular';

// other libraries
import { TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject } from "rxjs";

// components

// providers
import { ConversationService } from "../../services/conversation.service";
import { ChatGroupsService } from "../../services/chat-groups.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";

// models
import { ConversationMessage } from "../../models/conversation-message";
import { ChatGroupMessage } from "../../models/chat-group-message";
import { Conversation } from "../../models/conversation";

// config not using...
import { environment } from "../../../environments/environment";

// helpers
import { log } from '../../helpers/log';

@Component({
    selector: 'edit-message',
    templateUrl: './edit-message.component.html',
    styleUrls: ['./edit-message.component.scss']
})
export class EditMessageComponent implements OnInit, OnDestroy {

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * show form in modal
     *
     * @type boolean
     */
    @Input() modal: boolean = false;

    /**
     * message
     *
     * @type {Message}
     */
    @Input() message: ConversationMessage | ChatGroupMessage;

    /**
     * conversation
     *
     * @type {Conversation}
     */
    @Input() conversationId: number;

    /**
     * message
     *
     * @type {Message}
     */
    private messageOriginal: ConversationMessage | ChatGroupMessage;
    /**
     * validate state
     *
     * @type {boolean}
     */
    public validate: boolean = false;

    /**
     * Validation errors
     *
     * @type {object}
     */
    public validationErrors: object = {};

    /**
     * validation errors state
     *
     * @type {boolean}
     */
    public hasValidationErrors: boolean = false;

    /**
     * sending status
     *
     * @type {boolean}
     */
    public sending: boolean = false;

    @Output() onSended: EventEmitter<Conversation> = new EventEmitter();
    @Output() onCanceled: EventEmitter<ConversationMessage | ChatGroupMessage> = new EventEmitter();

    @Input() displayBigTextarea: boolean = false;

    /**
     * constructor
     *
     * @param TranslateService
     * @param NgZone
     * @param Events
     * @param PlatformService
     * @param OverlayService
     * @param ConversationService
     * @param ModalController
     */
    constructor(
        // public userService: UserService,
        public translate: TranslateService,
        public ngZone: NgZone,
        public appEvents: Events,
        public plt: PlatformService,
        public overlayService: OverlayService,
        public conversationService: ConversationService,
        public chatGroupsService: ChatGroupsService,
        public modalController: ModalController
    ) {
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        // this.dropZoneConfig.url = environment.api + '/newsfeed/upload?ngsw-bypass=true';
        if (this.message instanceof ConversationMessage) {
            this.messageOriginal = new ConversationMessage(this.message);
        } else if (this.message instanceof ChatGroupMessage) {
            this.messageOriginal = new ChatGroupMessage(this.message);
        }

        if (this.message.id && !this.modal) {
            this.displayBigTextarea = true;

            // don't scroll on mobile in modal...
            if (!this.modal) {
                setTimeout(() => {
                    let el = document.querySelector('.edit-message-' + this.message.id);
                    if (el) {
                        el.scrollIntoView();
                    }
                }, 250);
            }
        }
    }

    public edit(message: ConversationMessage | ChatGroupMessage) {
        this.message = message;
        if (this.message instanceof ConversationMessage) {
            this.messageOriginal = new ConversationMessage(this.message);
        } else if (this.message instanceof ChatGroupMessage) {
            this.messageOriginal = new ChatGroupMessage(this.message);
        }
    }

    public async openDestroyMessage(message: ConversationMessage | ChatGroupMessage) {
        this.overlayService.showConfirm(
            this.translate.instant('MESSAGE_DELETE_MODAL_TITLE'),
            this.translate.instant('MESSAGE_DELETE_MODAL_HINT'),
            () => { this.deleteMessage(message) },
            () => { },
            this.translate.instant('MESSAGE_DELETE_MODAL_BUTTON')
        );
    }

    /**
     * delete message
     *
     * @return void
     */
    public deleteMessage(message: ConversationMessage | ChatGroupMessage) {
        if (this.message instanceof ConversationMessage) {
            let subscription = this.conversationService.deleteMessage(message.id)
                .subscribe((response) => {
                    this.message.deleted_at = response.deleted_at;
                    this.message.edit = false;
                    this.displayBigTextarea = false;
                    if (this.modal) {
                        // close modal if used..
                        this.closeSaved();
                    }
                });
            this.plt.activeSubscriptions.push(subscription);
        } else if (this.message instanceof ChatGroupMessage) {
            // Chat group message
            let subscriptionDelete = this.chatGroupsService.deleteMessage(message.id, <ChatGroupMessage>message)
                .subscribe((response) => {
                    this.message.deleted_at = response.deleted_at;
                    this.message.edit = false;
                    this.displayBigTextarea = false;
                    if (this.modal) {
                        // close modal if used..
                        this.closeSaved();
                    }
                });
            this.plt.activeSubscriptions.push(subscriptionDelete);
        }
    }

    /**
     * send new message
     */
    public postMessage(valid) {
        if (this.message.id && !this.message.message.length) {
            this.openDestroyMessage(this.message);
            return false;
        }

        if (valid) {
            this.sending = true;
            this.validationErrors = {};
            this.validate = true;

            if (this.message instanceof ConversationMessage) {
                let subscription = this.conversationService.sendMessage(this.conversationId, this.message)
                    .subscribe((conversation: Conversation) => {
                        this.handleSuccess(conversation);
                    }, error => {
                        this.handleError(error);
                    });
                this.plt.activeSubscriptions.push(subscription);
            } else if (this.message instanceof ChatGroupMessage) {
                // Chat group message
                let subscriptionUpdate = this.chatGroupsService.updateMessage(this.message.id, this.message)
                    .subscribe((conversation: Conversation) => {
                        this.handleSuccess(conversation);
                    }, error => {
                        this.handleError(error);
                    });
                this.plt.activeSubscriptions.push(subscriptionUpdate);
            }
        }
    }

    private handleSuccess(response) {
        this.sending = false;
        // create new
        if (!this.message.id && this.message instanceof ConversationMessage) {
            this.message = null;
        }
        // edit
        else {
            this.message.edit = false;
        }

        if (this.message.message != this.messageOriginal.message) {
            this.message.edited();
        }

        this.onSended.emit(response);
        this.validate = false;
        this.displayBigTextarea = false;
        if (this.modal) {
            // close modal if used..
            this.closeSaved();
        }
    }

    private handleError(response) {
        this.sending = false;
        const data = response.error;
        this.overlayService.showError(data.message);

        if (data.error == 'Unauthorized') {
            if (this.modal) {
                // close modal if used..
                this.closeSaved();
            }
        }

        if (data.leave_group) {
            this.appEvents.publish('messages:refresh:list');
        }
    }

    /**
     * close modal window
     *
     */
    public backClicked(modal = true) {
        this.message.edit = false;

        if (modal) {
            this.modalController.dismiss({ action: 'close', message: this.messageOriginal });
        } else {
            this.message = this.messageOriginal;
            this.message.edit = false;
            this.onCanceled.emit(this.message);
        }
    }

    /**
     * close modal window
     *
     */
    public closeSaved() {
        this.modalController.dismiss({ action: 'close', message: this.message });
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.overlayService.closeModals();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
